import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Check, Trash2, TrendingUp, ArrowDownCircle, ArrowUpCircle, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { formatBRL, formatDate } from '@/lib/format';

function LedgerTable({ tipo, reload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { api.get(`/finance/ledger?tipo=${tipo}`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [tipo, reload]);
  const pay = async (id) => { try { await api.post(`/finance/ledger/${id}/pay`); toast.success('Baixado'); load(); } catch { toast.error('Erro'); } };
  const del = async (id) => { try { await api.delete(`/finance/ledger/${id}`); toast.success('Removido'); load(); } catch { toast.error('Erro'); } };
  const totalPend = items.filter((i) => !i.pago).reduce((a, i) => a + Number(i.valor || 0), 0);

  if (loading) return <p className="text-muted-foreground py-6 text-center">Carregando...</p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground">Pendente: <span className="font-semibold text-foreground tabular-nums">{formatBRL(totalPend)}</span></span>
      </div>
      {items.length === 0 ? <p className="text-muted-foreground py-8 text-center">Nenhum lançamento.</p> : (
        <Table data-testid={`ledger-table-${tipo}`}>
          <TableHeader><TableRow><TableHead>Descrição</TableHead><TableHead>Vencimento</TableHead><TableHead className="text-right">Valor</TableHead><TableHead>Status</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.entry_id}>
                <TableCell className="font-medium">{i.descricao}</TableCell>
                <TableCell className="text-muted-foreground">{formatDate(i.vencimento)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatBRL(i.valor)}</TableCell>
                <TableCell>{i.pago ? <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)]">Pago</Badge> : <Badge variant="secondary">Pendente</Badge>}</TableCell>
                <TableCell><div className="flex gap-1 justify-end">
                  {!i.pago && <Button variant="ghost" size="icon" onClick={() => pay(i.entry_id)} title="Dar baixa" data-testid={`ledger-pay-${i.entry_id}`}><Check className="h-4 w-4 text-[hsl(174_55%_28%)]" /></Button>}
                  <Button variant="ghost" size="icon" onClick={() => del(i.entry_id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function Commissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get('/finance/commissions').then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <p className="text-muted-foreground py-6 text-center">Carregando...</p>;
  if (items.length === 0) return <div className="py-10 text-center"><Award className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhuma comissão ainda.</p></div>;
  return (
    <Table data-testid="commissions-table">
      <TableHeader><TableRow><TableHead>Vendedor</TableHead><TableHead className="text-right">Vendas</TableHead><TableHead className="text-right">Total vendido</TableHead><TableHead className="text-right">Comissão</TableHead></TableRow></TableHeader>
      <TableBody>
        {items.map((c) => (
          <TableRow key={c.vendedor_id}>
            <TableCell className="font-medium">{c.vendedor_nome}</TableCell>
            <TableCell className="text-right tabular-nums">{c.qtd}</TableCell>
            <TableCell className="text-right tabular-nums">{formatBRL(c.total_vendas)}</TableCell>
            <TableCell className="text-right tabular-nums font-semibold text-[hsl(174_55%_24%)]">{formatBRL(c.total_comissao)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function Finance() {
  const [open, setOpen] = useState(false);
  const [reload, setReload] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tipo: 'pagar', descricao: '', valor: 0, vencimento: '', categoria: 'geral' });

  const save = async (e) => {
    e.preventDefault();
    if (!form.descricao) { toast.error('Informe a descrição'); return; }
    setSaving(true);
    try {
      await api.post('/finance/ledger', { ...form, valor: Number(form.valor || 0), pago: false });
      toast.success('Lançamento criado'); setOpen(false);
      setForm({ tipo: 'pagar', descricao: '', valor: 0, vencimento: '', categoria: 'geral' });
      setReload((r) => r + 1);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Financeiro</h2>
          <p className="text-sm text-muted-foreground">Contas a pagar e receber, fluxo de caixa e comissões.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="finance-new-entry"><Plus className="h-4 w-4 mr-2" /> Novo lançamento</Button>
      </div>

      <Card className="p-4">
        <Tabs defaultValue="receber">
          <TabsList>
            <TabsTrigger value="receber" data-testid="finance-tab-receber"><ArrowUpCircle className="h-4 w-4 mr-2" /> A receber</TabsTrigger>
            <TabsTrigger value="pagar" data-testid="finance-tab-pagar"><ArrowDownCircle className="h-4 w-4 mr-2" /> A pagar</TabsTrigger>
            <TabsTrigger value="comissoes" data-testid="finance-tab-comissoes"><Award className="h-4 w-4 mr-2" /> Comissões</TabsTrigger>
          </TabsList>
          <TabsContent value="receber" className="pt-4"><LedgerTable tipo="receber" reload={reload} /></TabsContent>
          <TabsContent value="pagar" className="pt-4"><LedgerTable tipo="pagar" reload={reload} /></TabsContent>
          <TabsContent value="comissoes" className="pt-4"><Commissions /></TabsContent>
        </Tabs>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo lançamento</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ledger-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="receber">A receber</SelectItem><SelectItem value="pagar">A pagar</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Descrição</Label><Input value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} className="mt-1.5" data-testid="ledger-descricao" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="mt-1.5" data-testid="ledger-valor" /></div>
              <div><Label>Vencimento</Label><Input type="date" value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} className="mt-1.5" /></div>
            </div>
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} data-testid="ledger-save">{saving ? 'Salvando...' : 'Salvar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
